<!--
// app/components/Shared/standings/StandingsLegend.vue
-->
<script setup lang="ts">
import type {
  StandingPlayoffColors,
  StandingPositionColor,
} from "~~/src/season-cluster/domain/models/01.Season";

type Props = {
  legend: StandingPlayoffColors[];
  large?: boolean;
  groupedColors?: boolean;
  isHomeLegend?: boolean;
  dark?: boolean;
  isChallenger?: boolean;
};

const props = withDefaults(defineProps<Props>(), { large: false, groupedColors: false });

const { $t } = useI18n();

const unifiedLegend = computed((): StandingPlayoffColors[] => {
  try {
    return Array.from(new Set(props.legend.map(e => JSON.stringify(e)))).map(e => JSON.parse(e));
  } catch (e) {
    console.error(e);
    return [];
  }
});

function getBgClass(playoffColor: StandingPositionColor) {
  //This function MUST be defined in the Vue file. In any other cases, Tailwind won't render the classes.
  switch (playoffColor) {
    case "green":
      return "!bg-[#10E217]";
    case "orange":
      return "!bg-amber-400";
    case "softOrange":
      return "!bg-[#EC7B39]";
    case "darkOrange":
      return "!bg-[#FD5900]";
    case "red":
      return "!bg-red-500";
    case "yellow":
      return "!bg-[#FFFB00]";
    case "brown":
      return "!bg-[#A8834E]";
    case "gray":
      return "!bg-gray-500";
    case "greenDashed":
      return "!bg-[repeating-linear-gradient(to_bottom,#10E217_0,#10E217_4px,_transparent_4px,_transparent_8px)]";
    case "yellowDashed":
      return "!bg-[repeating-linear-gradient(to_bottom,#FFFB00_0,#FFFB00_4px,_transparent_4px,_transparent_8px)]";
    case "darkOrangeDashed":
      return "!bg-[repeating-linear-gradient(to_bottom,#FD5900_0,#FD5900_8px,_transparent_8px,_transparent_12px)]";
    default:
      return "!bg-gray-800";
  }
}

function getTextClass(playoffColor: StandingPositionColor) {
  //This function MUST be defined in the Vue file. In any other cases, Tailwind won't render the classes.
  switch (playoffColor) {
    case "green":
    case "greenDashed":
      return "text-[#10E217]";
    case "orange":
      return "text-amber-400";
    case "softOrange":
      return "text-[#EC7B39]";
    case "darkOrange":
    case "darkOrangeDashed":
      return "text-[#FD5900]";
    case "red":
      return "text-red-500";
    case "yellow":
    case "yellowDashed":
      return "text-[#FFFB00]";
    case "brown":
      return "text-[#A8834E]";
    case "gray":
      return "text-gray-500";
    default:
      return "text-gray-800";
  }
}
</script>

<template>
  <div
    v-if="!groupedColors"
    :class="['flex flex-wrap', large ? 'gap-x-4 gap-y-2' : 'gap-x-4 gap-y-2', { '!gap-x-2': isHomeLegend }]"
  >
    <div v-for="el in unifiedLegend" :class="['flex items-center', large ? 'gap-x-2' : 'gap-x-1.5']">
      <div :class="[large ? 'w-1.5 h-5' : 'w-1 h-4', getBgClass(el.color)]" />
      <div
        :class="[
          { 'text-lg': large },
          isHomeLegend ? 'text-gray-400 text-sm' : dark ? 'text-black font-semibold' : getTextClass(el.color),
        ]"
      >
        {{ $t(`tables.legend.placement.${el.placement}`) }}
        {{ isChallenger && el.color.includes("Dashed") ? $t("tables.legend.placement.dashedInfo") : "" }}
      </div>
    </div>
  </div>

  <div v-else-if="groupedColors" :class="['flex flex-wrap items-center', large ? 'gap-3' : 'gap-1']">
    <div v-for="el in unifiedLegend">
      <div :class="[large ? 'w-1.5 h-5' : 'w-1 h-4', getBgClass(el.color)]" />
    </div>
    <slot />
  </div>
</template>

<style scoped></style>
